import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import aiService from '../services/aiService';
import { parseFile } from '../utils/pdfParser';
import ResumeViewer from './ResumeViewer';
import PaymentModal from './PaymentModal';
import DragDropUpload from './DragDropUpload';
import {
  FileText,
  Mail,
  Search,
  MessageSquare,
  BarChart3,
  Sparkles,
  ArrowRight,
  Download,
  Copy,
  Eye,
  Star,
  TrendingUp,
  Users,
  Target,
  Clock,
  CheckCircle,
  AlertCircle,
  Zap,
  Brain,
  Briefcase,
  PenTool,
  ArrowLeft,
  Home,
  User,
  Plus,
  Edit,
  Upload,
  ClipboardList
} from 'lucide-react';

const CareerHub = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('resume');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [showResumeViewer, setShowResumeViewer] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
  }, [user, navigate]);

  // Handle URL parameters for direct tab navigation
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['resume', 'cover-letter', 'job-matching', 'interview-prep', 'analytics'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleViewResume = (resumeId) => {
    console.log('🔍 CareerHub: handleViewResume called with:', resumeId);
    setSelectedResumeId(resumeId);
    setShowResumeViewer(true);
  };

  const handleCloseViewer = () => {
    setShowResumeViewer(false);
    setSelectedResumeId(null);
  };

  const handleDeleteResume = (resumeId) => {
    // Handle resume deletion
    console.log('Deleting resume:', resumeId);
  };

  const handleDownloadResume = async (resumeId, format = 'txt') => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/resume/${resumeId}/download?format=${format}`
      );

      if (!response.ok) {
        throw new Error('Failed to download resume');
      }

      // Create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `resume_${resumeId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Download failed:', error);
      setError('Download failed. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  if (!user) {
    return null; // Will redirect via useEffect
  }

  const tabs = [
    { id: 'resume', label: 'Resume Optimizer', icon: FileText, color: 'purple' },
    { id: 'cover-letter', label: 'Cover Letters', icon: Mail, color: 'green' },
    { id: 'job-matching', label: 'Job Matching', icon: Search, color: 'orange' },
    { id: 'interview-prep', label: 'Interview Prep', icon: MessageSquare, color: 'red' },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp, color: 'indigo' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'resume':
        return <ResumeOptimizerTab 
          user={user} 
          onViewResume={handleViewResume}
          onDownloadResume={handleDownloadResume}
        />;
      case 'cover-letter':
        return <CoverLetterTab user={user} />;
      case 'job-matching':
        return <JobMatchingTab user={user} />;
      case 'interview-prep':
        return <InterviewPrepTab user={user} />;
      case 'analytics':
        return <AnalyticsTab user={user} />;
      default:
        return <ResumeOptimizerTab 
          user={user} 
          onViewResume={handleViewResume}
          onDownloadResume={handleDownloadResume}
        />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center px-3 py-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </button>
              <div className="h-6 w-px bg-slate-300"></div>
              <h1 className="text-2xl font-bold text-slate-900">Shortlistly Career Hub</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <img
                  src={user?.photoURL || 'https://images.pexels.com/photos/5921483/pexels-photo-5921483.jpeg'}
                  alt="Profile"
                  className="w-8 h-8 rounded-full"
                />
                <span className="text-slate-700 font-medium">{user?.displayName}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 bg-white rounded-2xl p-2 shadow-lg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-3 sm:px-4 py-3 rounded-xl font-medium transition-all duration-300 flex-1 sm:flex-none justify-center sm:justify-start min-w-0 ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              <tab.icon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
              <span className="text-sm sm:text-base font-medium truncate">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderTabContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Resume Viewer Modal */}
      {showResumeViewer && selectedResumeId && (
        <ResumeViewer
          resumeId={selectedResumeId}
          user={user}
          onClose={handleCloseViewer}
          onDelete={handleDeleteResume}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
          user={user}
        />
      )}
    </div>
  );
};

// Resume Optimizer Tab Component with Enhanced Features
const ResumeOptimizerTab = ({ user, onViewResume, onDownloadResume }) => {
  const navigate = useNavigate();
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [targetIndustry, setTargetIndustry] = useState('');
  const [goalSelection, setGoalSelection] = useState('');
  const [uploadMethod, setUploadMethod] = useState('text');
  const [resumeFile, setResumeFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resumeHistory, setResumeHistory] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Load user subscription and history
  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      // Load subscription
      const subResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/subscription/${user.uid}`);
      const subData = await subResponse.json();
      setSubscription(subData);

      // Load resume history
      const historyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resume/history/${user.uid}`);
      const historyData = await historyResponse.json();
      setResumeHistory(historyData.optimizations || historyData.history || []);
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  };

  const handleFileUpload = async (file) => {
    setResumeFile(file);
    setError('🔄 Processing file...');
    
    try {
      const extractedText = await parseFile(file);
      if (extractedText && extractedText.length > 20) {
        setResumeText(extractedText);
        setError('');
        
        // Switch to text view to show the extracted content
        setUploadMethod('text');
        
        // Show success message briefly
        setError('✅ PDF processed successfully! Text extracted and ready for analysis.');
        setTimeout(() => setError(''), 3000);
        
        // Focus the textarea after a brief delay to allow DOM update
        setTimeout(() => {
          const textarea = document.querySelector('textarea');
          if (textarea) {
            textarea.focus();
            textarea.scrollTop = 0; // Scroll to top of extracted text
          }
        }, 100);
        
      } else {
        throw new Error('File appears to be empty or contains no readable text.');
      }
    } catch (error) {
      setError(`❌ ${error.message} Please paste your resume text instead.`);
      setResumeFile(null);
      setUploadMethod('text');
      throw error; // Re-throw to let DragDropUpload handle the error state
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    console.log('🎉 Career Hub payment success handler called');
    
    try {
      // Force proceed with optimization since payment was verified
      console.log('🚀 Proceeding with full optimization after payment...');
      setLoading(true);
      setError('');

      const response = await aiService.optimizeResume(
        resumeText,
        jobDescription,
        user?.uid || 'paid_user', // Use fallback if user state is lost
        targetIndustry
      );
      
      console.log('✅ Career Hub optimization completed:', response);
      setResult(response);
      
      // Reload user data in background
      setTimeout(loadUserData, 1000);
    } catch (error) {
      console.error('❌ Career Hub optimization error:', error);
      setError('Failed to optimize resume: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    setShowPaymentModal(true);
  };

  const goals = [
    {
      id: 'fresher',
      title: "I'm a Fresher",
      description: 'Optimize for entry-level positions',
      icon: '🎓'
    },
    {
      id: 'career-switch',
      title: 'Switching Careers',
      description: 'Transition to a new industry',
      icon: '🔄'
    },
    {
      id: 'job-specific',
      title: 'Tailor for Job Description',
      description: 'Optimize for specific role',
      icon: '🎯'
    }
  ];

  const handleOptimize = async () => {
    if (!resumeText.trim()) {
      setError('Please enter your resume text');
      return;
    }

    // Don't proceed if subscription is still loading
    if (subscription === null) {
      setError('Loading your subscription details...');
      return;
    }

    // Check subscription status
    const isPremium = subscription?.plan && subscription.plan !== 'free';
    
    if (!isPremium) {
      // Show payment modal for non-premium users
      setShowPaymentModal(true);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Full optimization for premium users
      const response = await aiService.optimizeResume(
        resumeText,
        jobDescription,
        user?.uid || '',
        targetIndustry
      );
      setResult(response);
      
      // Reload history after successful optimization
      setTimeout(loadUserData, 1000);
    } catch (error) {
      console.error('Optimization error:', error);
      setError('Failed to optimize resume. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  const isPremium = subscription?.plan !== 'free';

  return (
    <div className="space-y-8">
      {!result ? (
        <>
          {/* Resume History Section */}
          {resumeHistory.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Your Resume History</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {resumeHistory.slice(0, 3).map((resume, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-xl border">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-slate-900">
                        Resume #{resumeHistory.length - index}
                      </div>
                      <div className="text-sm text-slate-500">
                        {new Date(resume.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-lg font-bold text-blue-600">
                        {resume.ats_score}%
                      </div>
                      <div className="text-sm text-slate-600">ATS Score</div>
                    </div>
                    <div className="mt-2 flex space-x-2">
                      <button 
                        onClick={() => {
                          if (resume?.id && onViewResume) {
                            onViewResume(resume.id);
                          } else {
                            console.error('Resume ID missing or onViewResume not provided:', resume);
                          }
                        }}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                      >
                        <Eye className="w-3 h-3 inline mr-1" />
                        View
                      </button>
                      <button 
                        onClick={() => {
                          if (resume?.id && onDownloadResume) {
                            onDownloadResume(resume.id, 'pdf');
                          } else {
                            console.error('Resume ID missing or onDownloadResume not provided:', resume);
                          }
                        }}
                        className="text-xs px-2 py-1 bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors"
                      >
                        <Download className="w-3 h-3 inline mr-1" />
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Goal Selection */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-6">What's your goal?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {goals.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => setGoalSelection(goal.id)}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                    goalSelection === goal.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <div className="text-2xl mb-2">{goal.icon}</div>
                  <h4 className="font-semibold text-slate-900 mb-1">{goal.title}</h4>
                  <p className="text-sm text-slate-600">{goal.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Enhanced Input Section with Drag & Drop */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Resume Content</h3>
              </div>
              
              {/* Upload Method Toggle */}
              <div className="flex mb-6 bg-slate-100 rounded-2xl p-2">
                <button
                  onClick={() => setUploadMethod('text')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                    uploadMethod === 'text' 
                      ? 'bg-white text-blue-600 shadow-md' 
                      : 'text-slate-600'
                  }`}
                >
                  <ClipboardList className="w-5 h-5 inline mr-2" />
                  Paste Text
                </button>
                <button
                  onClick={() => setUploadMethod('file')}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                    uploadMethod === 'file' 
                      ? 'bg-white text-blue-600 shadow-md' 
                      : 'text-slate-600'
                  }`}
                >
                  <Upload className="w-5 h-5 inline mr-2" />
                  Upload File
                </button>
              </div>

              {uploadMethod === 'file' ? (
                <DragDropUpload 
                  onFileUpload={handleFileUpload}
                  acceptedTypes=".pdf,.doc,.docx,.txt"
                  maxSize={10 * 1024 * 1024} // 10MB
                />
              ) : (
                <div className="space-y-3">
                  {/* Show file upload success indicator */}
                  {resumeFile && (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-xl">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                        <span className="text-green-700 font-medium">
                          📄 {resumeFile.name} processed successfully
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setResumeFile(null);
                          setResumeText('');
                          setUploadMethod('file');
                        }}
                        className="text-green-600 hover:text-green-800 font-medium"
                      >
                        Upload Different File
                      </button>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Your Resume Text *
                    </label>
                    <textarea
                      value={resumeText}
                      onChange={(e) => setResumeText(e.target.value)}
                      className={`w-full h-40 p-3 border rounded-xl focus:ring-2 focus:border-transparent resize-none transition-all ${
                        resumeFile 
                          ? 'border-green-300 bg-green-50 focus:ring-green-500' 
                          : 'border-slate-300 focus:ring-blue-500'
                      }`}
                      placeholder={resumeFile ? "Text extracted from your uploaded file. You can edit it here..." : "Paste your complete resume here..."}
                    />
                  </div>
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Target Industry (Optional)
                </label>
                <input
                  type="text"
                  value={targetIndustry}
                  onChange={(e) => setTargetIndustry(e.target.value)}
                  className="w-full p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Technology, Healthcare, Finance"
                />
              </div>
            </div>

            {/* Job Description Section */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl flex items-center justify-center mr-4">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Job Description</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Job Description (Optional but Recommended)
                </label>
                <textarea
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  className="w-full h-40 p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none mb-6"
                  placeholder="Paste the job description to optimize your resume for specific roles..."
                />
              </div>

              {/* Subscription Status & CTA */}
              <div className="mt-6">
                {!isPremium && (
                  <div className="mb-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl">
                    <div className="flex items-center mb-2">
                      <AlertCircle className="w-5 h-5 text-orange-600 mr-2" />
                      <span className="font-semibold text-orange-900">Upgrade Required</span>
                    </div>
                    <div className="text-sm text-orange-800">
                      Resume optimization requires a paid plan. 
                      <strong> Click below to upgrade for just ₹199</strong> and unlock full AI optimization, downloads, and unlimited resumes.
                    </div>
                  </div>
                )}

                <button
                  onClick={handleOptimize}
                  disabled={loading || !resumeText.trim()}
                  className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Brain className="w-5 h-5 mr-2 animate-spin" />
                      {isPremium ? 'Optimizing with AI...' : 'Analyzing with AI...'}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      {isPremium ? 'Optimize Resume with AI' : 'Upgrade & Optimize Resume'}
                    </>
                  )}
                </button>
              </div>

              {error && (
                <div className={`mt-4 p-3 border rounded-xl text-sm ${
                  error.includes('✅') || error.includes('successfully') 
                    ? 'bg-green-50 border-green-200 text-green-700' 
                    : error.includes('🔄') || error.includes('Processing')
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  {error}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <ResumeResults 
          result={result} 
          onReset={() => setResult(null)} 
          onCopy={copyToClipboard}
          isPremium={!result?.isPreview}
          subscription={subscription}
          onUpgrade={handleUpgrade}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
          userId={user?.uid}
        />
      )}
    </div>
  );
};

// Resume Results Component with Upgrade CTA
const ResumeResults = ({ result, onReset, onCopy, isPremium, subscription, onUpgrade }) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-slate-900">
            {isPremium ? 'Optimization Complete!' : 'Analysis Complete!'}
          </h3>
          <p className="text-slate-600">
            {isPremium 
              ? 'Your resume has been optimized with AI' 
              : 'Preview analysis - upgrade for full optimization'
            }
          </p>
        </div>
        <div className="flex gap-3">
          {isPremium && (
            <button
              onClick={() => onCopy(result.optimized_resume || result.preview_text)}
              className="flex items-center px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </button>
          )}
          <button
            onClick={onReset}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            {isPremium ? 'Optimize Another' : 'Analyze Another'}
          </button>
        </div>
      </div>

      {/* Upgrade CTA for Free Users - Matching Landing Page Style */}
      {!isPremium && (
        <div className="bg-gradient-to-r from-red-50 via-orange-50 to-yellow-50 border-2 border-red-200 rounded-2xl p-6">
          <div className="text-center">
            <div className="flex items-center justify-center mb-3">
              <AlertCircle className="w-8 h-8 text-red-600 mr-3" />
              <h4 className="text-2xl font-bold text-red-900">🚨 Your Resume Has Critical Issues!</h4>
            </div>
            <p className="text-red-800 mb-4 text-lg">
              We found <strong>{result.critical_issues?.length || 5} critical issues</strong> that are blocking interviews. 
              Your current ATS score of <strong>{result.ats_score}%</strong> means <strong>73% rejection rate</strong>!
            </p>
            
            {/* Issue Preview */}
            <div className="grid md:grid-cols-2 gap-4 mb-6 text-left">
              <div className="bg-white rounded-xl p-4 border border-red-200">
                <h5 className="font-bold text-red-900 mb-2">💼 Missing Keywords</h5>
                <p className="text-red-700 text-sm">Your resume is missing 8+ essential keywords recruiters search for</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-red-200">
                <h5 className="font-bold text-red-900 mb-2">📊 Low ATS Score</h5>
                <p className="text-red-700 text-sm">Only {result.ats_score}% compatibility with Applicant Tracking Systems</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/pricing')}
                className="px-8 py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold rounded-xl hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                🔥 Fix All Issues Now - Just ₹199
              </button>
              <div className="text-center">
                <div className="text-sm font-semibold text-red-700">✅ Get 90%+ ATS Score</div>
                <div className="text-sm text-red-600">✅ PDF/DOCX Downloads</div>
                <div className="text-sm text-red-600">✅ Fix All {result.critical_issues?.length || 5} Issues</div>
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="text-red-600 text-sm">
                ⚠️ <strong>Don't let these issues cost you your dream job!</strong> 
                Most recruiters spend only 6 seconds on each resume.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Results Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Optimized Resume or Preview */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg">
          <h4 className="text-lg font-semibold text-slate-900 mb-4">
            {isPremium ? 'Optimized Resume' : 'Resume Preview'}
          </h4>
          <div className="bg-slate-50 rounded-xl p-4 max-h-96 overflow-y-auto">
            {isPremium ? (
              <pre className="whitespace-pre-wrap text-sm text-slate-700">
                {result.optimized_resume}
              </pre>
            ) : (
              <div className="relative">
                <pre className="whitespace-pre-wrap text-sm text-slate-700">
                  {result.preview_text || "Your optimized resume preview will appear here..."}
                </pre>
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent"></div>
                <div className="text-center mt-4">
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <p className="text-slate-600 text-sm mb-2">Unlock full optimization</p>
                    <button
                      onClick={onUpgrade}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Upgrade to see complete resume
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats and Insights */}
        <div className="space-y-6">
          {/* ATS Score */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h4 className="text-lg font-semibold text-slate-900 mb-4">ATS Score</h4>
            <div className="text-center">
              <div className={`text-4xl font-bold mb-2 ${isPremium ? 'text-green-600' : 'text-orange-600'}`}>
                {result.ats_score}%
              </div>
              {!isPremium && (
                <div className="text-sm text-red-600 font-medium mb-2">
                  ⚠️ Capped at 67% (Free Plan)
                </div>
              )}
              <div className="text-sm text-slate-600">
                {result.original_score && `Improved from ${result.original_score}%`}
              </div>
            </div>
          </div>

          {/* Critical Issues */}
          {result.critical_issues && (
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h4 className="text-lg font-semibold text-slate-900 mb-4">
                Critical Issues {!isPremium && '(Preview)'}
              </h4>
              <div className="space-y-2">
                {result.critical_issues.slice(0, isPremium ? undefined : 3).map((issue, index) => (
                  <div key={index} className="flex items-start">
                    <AlertCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{issue}</span>
                  </div>
                ))}
                {!isPremium && (
                  <div className="text-center pt-2">
                    <div className="text-xs text-slate-500 mb-2">
                      💼 Missing {((result.critical_issues?.length || 5) - 3)} more critical issues
                    </div>
                    <button
                      onClick={onUpgrade}
                      className="text-sm text-red-600 hover:text-red-700 font-semibold"
                    >
                      🚨 See all {result.critical_issues?.length || 5} critical issues →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Keywords Matched */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h4 className="text-lg font-semibold text-slate-900 mb-4">
              Keywords Matched {!isPremium && '(Limited)'}
            </h4>
            <div className="space-y-2">
              {result.keyword_matches?.slice(0, isPremium ? undefined : 3).map((keyword, index) => (
                <div key={index} className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  <span className="text-sm text-slate-700">{keyword}</span>
                </div>
              ))}
              {!isPremium && (
                <div className="text-center pt-2">
                  <div className="text-xs text-slate-500 mb-2">
                    💼 Missing {((result.keyword_matches?.length || 10) + 5)} more essential keywords
                  </div>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="text-sm text-blue-600 hover:text-blue-700 font-semibold"
                  >
                    Unlock all keyword optimization →
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Improvements */}
          {result.improvements && (
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h4 className="text-lg font-semibold text-slate-900 mb-4">
                Improvements {!isPremium && '(Preview)'}
              </h4>
              <div className="space-y-2">
                {result.improvements.slice(0, isPremium ? undefined : 2).map((improvement, index) => (
                  <div key={index} className="flex items-start">
                    <Sparkles className="w-4 h-4 text-blue-500 mr-2 mt-0.5" />
                    <span className="text-sm text-slate-700">{improvement}</span>
                  </div>
                ))}
                {!isPremium && (
                  <div className="text-center pt-2">
                    <div className="text-xs text-slate-500 mb-2">
                      📈 {((result.improvements?.length || 8) - 2)} more optimization strategies available
                    </div>
                    <button
                      onClick={() => navigate('/pricing')}
                      className="text-sm text-purple-600 hover:text-purple-700 font-semibold"
                    >
                      Get all AI improvements →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Placeholder components for other tabs
const CoverLetterTab = ({ user }) => (
  <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
    <Mail className="w-16 h-16 text-green-500 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-slate-900 mb-2">Cover Letter Generator</h3>
    <p className="text-slate-600 mb-4">AI-powered cover letter generation for {user?.displayName}</p>
    <button className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors">
      Coming Soon
    </button>
  </div>
);

const JobMatchingTab = ({ user }) => (
  <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
    <Search className="w-16 h-16 text-orange-500 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-slate-900 mb-2">Job Matching</h3>
    <p className="text-slate-600 mb-4">Intelligent job matching for {user?.displayName}</p>
    <button className="px-6 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors">
      Coming Soon
    </button>
  </div>
);

const InterviewPrepTab = ({ user }) => (
  <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
    <MessageSquare className="w-16 h-16 text-red-500 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-slate-900 mb-2">Interview Preparation</h3>
    <p className="text-slate-600 mb-4">AI interview prep tools for {user?.displayName}</p>
    <button className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors">
      Coming Soon
    </button>
  </div>
);

const AnalyticsTab = ({ user }) => (
  <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
    <BarChart3 className="w-16 h-16 text-indigo-500 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-slate-900 mb-2">Analytics</h3>
    <p className="text-slate-600 mb-4">Detailed analytics and insights for {user?.displayName}</p>
    <button className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors">
      Coming Soon
    </button>
  </div>
);

export default CareerHub;
